import { useEffect, useRef, useState } from 'react';
import ShelfQrScanner from './ShelfQrScanner';
import { dhlPickupStart, dhlPickupComplete, dhlNotiz, dhlScanCode, dhlKurz, istDhlPaket, istDhlCode, dt } from '../services/dhlBridge';

/**
 * 🧪 DHL-Ausgabe (Experiment): Prüft den Abholcode / QR-Code des Kunden oder den Paket-Barcode im
 * DHL-ServicePoint-Portal (`POST /servicepoint-api/customer/hand-out/validation`) und bucht danach
 * die Übergabe (`POST /servicepoint-api/customer/hand-out`) + Ausgabe in PaketPilot.
 * Rückfall: Antwortet DHL nicht oder verlangt eine Ausweisprüfung im Portal, kann das Paket jederzeit
 * mit einem Tipp „Ohne DHL ausgeben" in PaketPilot ausgegeben werden.
 */
export default function DhlAusgabe({ paket, pakete, colors, language, onPaketAusgeben, onClose }) {
  const T = dt(language);
  const dark = colors?.darkMode;
  const f = { text: dark ? '#fff' : '#23272f', sub: dark ? '#aaa' : '#666', rand: dark ? '#444' : '#ddd', feld: dark ? '#1b1f27' : '#fff', zeile: dark ? '#2d333b' : '#f5f6f8' };

  const gespeicherterCode = dhlScanCode(paket);
  const [phase, setPhase] = useState('code'); // code | sucht | liste | laeuft | fertig | rueckfall
  const [pin, setPin] = useState('');
  const [fehler, setFehler] = useState('');
  const [dhlParcels, setDhlParcels] = useState([]); // [{id, barcode, printlessCode, shown, name, pickupCode, requiresConfirmation}]
  const [customerName, setCustomerName] = useState('');
  const [ergebnis, setErgebnis] = useState({}); // id -> 'ok' | 'fehler'

  const ausgebenRef = useRef(onPaketAusgeben); ausgebenRef.current = onPaketAusgeben;
  const paketeRef = useRef(pakete); paketeRef.current = pakete;
  const lebt = useRef(true);
  useEffect(() => () => { lebt.current = false; }, []);

  const norm = (s) => String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
  const gleich = (a, b) => String(a || '').trim().toLowerCase().replace(/\s+/g, ' ') === String(b || '').trim().toLowerCase().replace(/\s+/g, ' ');
  const offeneDhl = () => (paketeRef.current || []).filter((p) => p && !p.deleted && !p.ausgegebenAm && !p.zurueckgeschickt && istDhlPaket(p));

  const meins = (v) => {
    if (!v) return null;
    const c1 = norm(v.barcode || v.id);
    const c2 = norm(v.printlessCode);
    const liste = offeneDhl();
    const perCode = liste.find((p) => {
      const pt = norm(p.trackingNumber);
      if (pt && ((c1 && (pt === c1 || pt.includes(c1) || c1.includes(pt))) || (c2 && (pt === c2 || pt.includes(c2) || c2.includes(pt))))) return true;
      const kurz = dhlKurz(c1 || c2);
      return kurz && String(p.name || '').toUpperCase().includes(kurz);
    });
    if (perCode) return perCode;
    if (v.name) {
      const perName = liste.filter((p) => gleich(p.name, v.name));
      if (perName.length === 1) return perName[0];
    }
    return null;
  };

  const starte = async (code) => {
    const c = String(code || '').trim();
    if (!c || phase === 'sucht') return;
    setFehler('');
    setPhase('sucht');
    const r = await dhlPickupStart(c);
    if (!lebt.current) return;
    if (r.ok && Array.isArray(r.parcels) && r.parcels.length > 0) {
      setDhlParcels(r.parcels);
      setCustomerName(r.customerName || paket?.name || '');
      setPhase('liste');
      return;
    }
    if (r.reason === 'rejected') {
      setFehler(`${T('abgelehnt')}${r.message ? ': ' + r.message : ''}`);
      setPin('');
      setPhase('code');
      return;
    }
    setFehler(r.message || r.reason || '');
    setPhase('rueckfall');
  };

  const uebergeben = async () => {
    setPhase('laeuft');
    const r = await dhlPickupComplete({
      parcels: dhlParcels,
      customerName: customerName || paket?.name || 'Klant',
      code: dhlParcels[0]?.barcode || gespeicherterCode,
    });
    if (!lebt.current) return;
    if (!r.ok) {
      const nextErr = {};
      for (const v of dhlParcels) nextErr[v.id] = 'fehler';
      setErgebnis(nextErr);
      setFehler(r.message || r.reason || '');
      setPhase('rueckfall');
      return;
    }
    const nextOk = {};
    for (const v of dhlParcels) nextOk[v.id] = 'ok';
    setErgebnis(nextOk);

    const ausgegebenSet = new Set();
    for (const v of dhlParcels) {
      const p = meins(v) || (dhlParcels.length === 1 ? paket : null);
      if (p && !ausgegebenSet.has(p.id)) {
        ausgegebenSet.add(p.id);
        await ausgebenRef.current(p);
        dhlNotiz('ausgabe_paketpilot', dhlScanCode(p) || v.barcode, { ok: true });
      }
    }
    if (!lebt.current) return;
    setPhase('fertig');
  };

  const ohneDhl = async () => {
    const rest = dhlParcels.length
      ? dhlParcels.filter((v) => ergebnis[v.id] !== 'ok').map((v) => meins(v)).filter(Boolean)
      : [paket];
    const zielPakete = rest.length ? rest : [paket];
    for (const p of zielPakete) {
      if (!p) continue;
      dhlNotiz('rueckfall', dhlScanCode(p), { reason: 'ausgabe', message: `Ausgabe ohne DHL: ${fehler || 'manuell übersprungen'}`.slice(0, 200) });
      await ausgebenRef.current(p);
      dhlNotiz('ausgabe_paketpilot', dhlScanCode(p), { ok: true });
      await new Promise((r) => setTimeout(r, 150));
    }
    onClose();
  };

  const feld = { width: '100%', padding: '10px', borderRadius: 8, border: `1px solid ${f.rand}`, fontSize: 16, background: f.feld, color: f.text, boxSizing: 'border-box' };
  const knopf = (primary, farbe) => ({ width: '100%', padding: '12px', borderRadius: 10, marginTop: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer', border: primary ? 'none' : `1px solid ${f.rand}`, background: primary ? (farbe || '#d40511') : 'transparent', color: primary ? '#fff' : f.text });
  const gehoertDazu = !dhlParcels.length || dhlParcels.some((v) => meins(v)?.id === paket?.id || (gespeicherterCode && norm(v.barcode) === norm(gespeicherterCode)));
  const brauchtAusweis = dhlParcels.some((v) => Array.isArray(v.requiresConfirmation) && v.requiresConfirmation.some((x) => x === 'documentType' || x === 'documentNumber'));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 7600, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-testid="dhl-ausgabe" data-phase={phase} style={{ background: dark ? '#23272f' : '#fff', color: f.text, borderRadius: 14, width: '100%', maxWidth: 520, maxHeight: '92vh', overflowY: 'auto', padding: 18, boxShadow: '0 10px 40px rgba(0,0,0,0.4)' }}>
        <b style={{ fontSize: 18 }}>🧪 {T('ausgabeTitel')}</b>
        <div data-testid="da-live" style={{ marginTop: 8, padding: '6px 10px', borderRadius: 8, background: '#c62828', color: '#fff', fontSize: 13, fontWeight: 700 }}>🔴 {T('liveBadge')}</div>
        <div style={{ fontSize: 14, color: f.sub, marginTop: 4 }}>{paket.name}{paket.regalplatz ? ` · 📍 ${paket.regalplatz}` : ''}</div>

        {phase === 'code' && (
          <div>
            {istDhlCode(gespeicherterCode) && (
              <button
                type="button"
                data-testid="da-direkt"
                onClick={() => starte(gespeicherterCode)}
                style={knopf(true, '#d40511')}
              >
                {T('ausgabeDirektKnopf', { code: dhlKurz(gespeicherterCode) })}
              </button>
            )}
            <div style={{ fontSize: 13, color: f.sub, marginTop: 10, lineHeight: 1.5 }}>{T('ausgabeHinweis')}</div>
            <div style={{ marginTop: 10 }}>
              <ShelfQrScanner
                active
                eingebettet
                formats={['qr_code', 'code_128', 'itf', 'data_matrix', 'code_39', 'ean_13', 'ean_8', 'codabar']}
                onCode={(text) => starte(text)}
              />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); starte(pin); }} style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <input data-testid="da-pin" placeholder={T('pin')} value={pin} onChange={(e) => setPin(e.target.value)} style={feld} />
              <button type="submit" data-testid="da-weiter" style={{ padding: '0 16px', borderRadius: 8, border: 'none', background: '#d40511', color: '#fff', fontWeight: 600, cursor: 'pointer' }}>{T('weiter')}</button>
            </form>
            <button type="button" data-testid="da-ohne-direkt" onClick={ohneDhl} style={knopf(false)}>{T('ohneDhlAusgeben')}</button>
          </div>
        )}
        {phase === 'sucht' && <div style={{ marginTop: 16, fontSize: 15 }}>⏳ {T('sucht')}</div>}

        {(phase === 'liste' || phase === 'laeuft' || phase === 'fertig' || (phase === 'rueckfall' && dhlParcels.length > 0)) && (
          <div data-testid="da-liste" style={{ marginTop: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{T('pakete')} ({dhlParcels.length})</div>
            {dhlParcels.map((v) => {
              const p = meins(v);
              return (
                <div key={v.id} data-ergebnis={ergebnis[v.id] || ''} style={{ padding: '8px 10px', borderRadius: 8, background: f.zeile, marginTop: 6, fontSize: 14 }}>
                  <b>{v.shown}</b> <span style={{ color: f.sub, fontSize: 12 }}>{v.barcode}</span>{v.name ? ` · ${v.name}` : ''}
                  <div style={{ fontSize: 13, color: f.sub, marginTop: 2 }}>
                    {p ? `📍 ${p.regalplatz || '—'}` : `⚠️ ${T('nichtInPP')}`}
                    {ergebnis[v.id] === 'ok' && <span style={{ color: '#2e7d32' }}> · ✅ {T('bestaetigt')}</span>}
                    {ergebnis[v.id] === 'fehler' && <span style={{ color: '#c62828' }}> · ✗ {T('bestaetigtNicht')}</span>}
                  </div>
                </div>
              );
            })}
            {!gehoertDazu && <div style={{ fontSize: 13, color: '#c62828', marginTop: 8 }}>⚠️ {paket.name}: {T('nichtBeiAbholer')}</div>}
            {brauchtAusweis && <div style={{ fontSize: 13, color: '#b26a00', marginTop: 8 }}>{T('ausweisHinweis')}</div>}
          </div>
        )}

        {fehler && <div data-testid="da-fehler" style={{ marginTop: 10, fontSize: 14, color: '#c62828' }}>{fehler}</div>}
        {phase === 'rueckfall' && <div style={{ marginTop: 8, fontSize: 13, color: f.sub, lineHeight: 1.5 }}>{T('rueckfallAusgabe')}</div>}

        {phase === 'liste' && <button type="button" data-testid="da-uebergeben" onClick={uebergeben} style={knopf(true, '#d40511')}>{T('uebergeben')}</button>}
        {phase === 'liste' && <button type="button" data-testid="da-ohne-liste" onClick={ohneDhl} style={knopf(false)}>{T('ohneDhlAusgeben')}</button>}
        {phase === 'laeuft' && <div style={{ marginTop: 14, fontSize: 15 }}>⏳ {T('sucht')}</div>}
        {phase === 'rueckfall' && <button type="button" data-testid="da-ohne" onClick={ohneDhl} style={knopf(true, '#b26a00')}>{T('ohneDhlAusgeben')}</button>}
        {phase === 'fertig'
          ? <button type="button" data-testid="da-fertig" onClick={onClose} style={knopf(true, '#2e7d32')}>{T('fertig')}</button>
          : phase !== 'laeuft' && phase !== 'sucht' && <button type="button" data-testid="da-abbrechen" onClick={onClose} style={knopf(false)}>{T('abbrechen')}</button>}
      </div>
    </div>
  );
}

